import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Lock, ShieldCheck } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  PasswordInput,
} from '@mlm/ui';
import { api, restoreSession } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';

type EnrollResponse = { otpauthUrl: string; qrDataUrl: string };

export function TwoFactorTab() {
  const user = useAuthStore((s) => s.user);
  const requiredByTenant = user?.tenantPolicy.member2faRequired ?? false;

  const [stage, setStage] = useState<'idle' | 'enrolling' | 'disabling'>('idle');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrollment, setEnrollment] = useState<EnrollResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enrollMut = useMutation({
    mutationFn: async () => (await api.post<EnrollResponse>('/auth/2fa/enroll', { password })).data,
    onSuccess: (data) => {
      setEnrollment(data);
      setError(null);
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error ?? 'Failed to enroll.');
    },
  });

  const verifyMut = useMutation({
    meta: { successMessage: 'Two-factor authentication enabled.' },
    mutationFn: async () => (await api.post('/auth/2fa/verify', { code })).data,
    onSuccess: async () => {
      setEnrollment(null);
      setStage('idle');
      setCode('');
      setPassword('');
      setError(null);
      await restoreSession();
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error ?? 'Invalid code.');
    },
  });

  const disableMut = useMutation({
    meta: { successMessage: 'Two-factor authentication disabled.' },
    mutationFn: async () => (await api.post('/auth/2fa/disable', { password, code })).data,
    onSuccess: async () => {
      setStage('idle');
      setPassword('');
      setCode('');
      setError(null);
      await restoreSession();
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error ?? 'Failed to disable.');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" /> Two-factor authentication
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <span>Status:</span>
          {user?.totpEnabled ? <Badge>Enabled</Badge> : <Badge variant="outline">Disabled</Badge>}
          {requiredByTenant && (
            <Badge variant="secondary" className="text-xs">
              <Lock className="h-3 w-3" /> Required by tenant
            </Badge>
          )}
        </div>

        {!user?.totpEnabled && stage === 'idle' && (
          <Button onClick={() => setStage('enrolling')} size="sm">
            Enroll 2FA
          </Button>
        )}
        {user?.totpEnabled && stage === 'idle' && !requiredByTenant && (
          <Button onClick={() => setStage('disabling')} size="sm" variant="outline">
            Disable 2FA
          </Button>
        )}
        {requiredByTenant && user?.totpEnabled && (
          <p className="text-xs text-muted-foreground">
            Your tenant requires 2FA — it can't be turned off here.
          </p>
        )}

        {stage === 'enrolling' && !enrollment && (
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs text-muted-foreground">Confirm password</label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => enrollMut.mutate()}
                disabled={!password || enrollMut.isPending}
                size="sm"
              >
                {enrollMut.isPending ? 'Generating…' : 'Generate QR code'}
              </Button>
              <Button
                onClick={() => {
                  setStage('idle');
                  setPassword('');
                  setError(null);
                }}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {stage === 'enrolling' && enrollment && (
          <div className="space-y-3 pt-2">
            <p className="text-xs text-muted-foreground">
              Scan with your authenticator app (Google Authenticator, 1Password, Authy), then enter
              the 6-digit code.
            </p>
            <div className="bg-white p-3 rounded-md inline-block">
              <img src={enrollment.qrDataUrl} alt="2FA QR code" className="h-40 w-40" />
            </div>
            <div className="text-[10px] text-muted-foreground font-mono break-all">
              {enrollment.otpauthUrl}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">6-digit code</label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => verifyMut.mutate()}
                disabled={code.length !== 6 || verifyMut.isPending}
                size="sm"
              >
                {verifyMut.isPending ? 'Verifying…' : 'Verify + activate'}
              </Button>
              <Button
                onClick={() => {
                  setEnrollment(null);
                  setCode('');
                  setError(null);
                }}
                variant="outline"
                size="sm"
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {stage === 'disabling' && (
          <div className="space-y-3 pt-2">
            <p className="text-xs text-muted-foreground">
              Enter your password and a current 6-digit code to disable 2FA.
            </p>
            <div>
              <label className="text-xs text-muted-foreground">Password</label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">6-digit code</label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => disableMut.mutate()}
                disabled={!password || code.length !== 6 || disableMut.isPending}
                size="sm"
                variant="destructive"
              >
                {disableMut.isPending ? 'Disabling…' : 'Disable'}
              </Button>
              <Button
                onClick={() => {
                  setStage('idle');
                  setPassword('');
                  setCode('');
                  setError(null);
                }}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {error && <p className="text-xs text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
